// ANGEL FLIGHT — DebriefScreen (DOM). Graded after-action report.
//
// FLIR-styled DOM port of the old Phaser DebriefScene, now a real game's debrief:
// a big AREA CLEAR (green) / MISSION FAILED (warn) verdict, a prominent colour-
// coded LETTER GRADE (S gold → D red) with the score + a NEW BEST! flash when it
// beat the persisted best, the full run-stat grid (kills/headshots/combo/scrip/
// salvage/time + friendly fire), an EARNED MEDALS row of labelled chips, and the
// way out — a primary CONTINUE plus an optional ARMORY shortcut. The grade,
// score, and medals all come from config/grading.ts (pure); the MissionResult is
// passed in already scored + best-flagged by the GameLoop.

import type { Screen } from '../Router';
import type { MissionResult } from '../../core/types';
import { earnedMedals, gradeForScore, type Grade, type Medal } from '../../config/grading';
import {
  FLIR,
  FONT,
  formatTime,
  makeButton,
  makePanel,
  makeScreenRoot,
  makeTitle,
  el,
  type ButtonHandle,
} from './ui';

export interface DebriefScreenOptions {
  /** The resolved mission outcome to display (already scored + best-flagged). */
  result: MissionResult;
  /** The wallet AFTER this run banked (save.scrip/salvage post-write) — shown
   *  so the player sees the run's pay actually land somewhere spendable. */
  wallet?: { scrip: number; salvage: number };
  /** Primary route onward (back to the menu / next mission). */
  onContinue: () => void;
  /** Optional shortcut into the Armory to spend the freshly banked scrip. */
  onArmory?: () => void;
  /** Immediate re-deploy into the SAME mission (one tap from the debrief —
   *  no menu round-trip). Shown for failed and cleared runs alike. */
  onRetry?: () => void;
}

/** Grade → FLIR colour. S gold, A bright green, B dim green, C amber, D red. */
const GRADE_COLOR: Record<Grade, string> = {
  S: FLIR.accent,
  A: FLIR.green,
  B: FLIR.greenDim,
  C: '#ffb061',
  D: FLIR.warn,
};

export class DebriefScreen implements Screen<void> {
  private readonly opts: DebriefScreenOptions;
  private root: HTMLDivElement | null = null;
  private continueBtn: ButtonHandle | null = null;
  private armoryBtn: ButtonHandle | null = null;
  private retryBtn: ButtonHandle | null = null;

  constructor(opts: DebriefScreenOptions) {
    this.opts = opts;
  }

  mount(parent: HTMLElement): void {
    const { result } = this.opts;
    const s = result.stats;
    const score = result.score;
    const grade = gradeForScore(score);
    const gradeColor = GRADE_COLOR[grade];
    // prevBest keeps the NEW BEST medal honest (it was read BEFORE banking) —
    // without it the medal would award on every scoring run, including fails.
    const medals = earnedMedals(result, result.prevBest);

    const root = makeScreenRoot();
    root.style.gap = '18px';

    // Endless (Last Stand) ends on the bird going down, but that's the mode's
    // natural finish, not a wipe — frame it as a neutral score result, not a red
    // MISSION FAILED. Survive/boss keep the clear/fail verdict.
    const endless = result.objective === 'endless';
    const verdict = endless
      ? makeTitle('LAST STAND — OVERRUN', FLIR.accent)
      : makeTitle(
          result.cleared ? 'AREA CLEAR' : 'MISSION FAILED',
          result.cleared ? FLIR.green : FLIR.warn,
        );

    // ── Grade hero: the big letter + score + optional NEW BEST! flash ────────
    const gradeRow = el('div', {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '20px',
      flexWrap: 'wrap',
    });

    const gradeBadge = el(
      'div',
      {
        font: `800 clamp(64px, 16vw, 120px)/0.9 ${FONT}`,
        letterSpacing: '0.04em',
        color: gradeColor,
        textShadow: `0 0 24px ${gradeColor}, 0 0 6px rgba(0,0,0,0.9)`,
        border: `2px solid ${gradeColor}`,
        borderRadius: '14px',
        padding: '8px 26px',
        minWidth: '120px',
        textAlign: 'center',
      },
      grade,
    );

    const scoreCol = el('div', {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: '6px',
    });
    scoreCol.append(
      el(
        'div',
        { font: `600 clamp(12px, 2.2vw, 14px)/1 ${FONT}`, letterSpacing: '0.06em', color: FLIR.greenDim },
        'MISSION SCORE',
      ),
      el(
        'div',
        { font: `800 clamp(28px, 6vw, 44px)/1 ${FONT}`, letterSpacing: '0.08em', color: FLIR.accent },
        `${score}`,
      ),
    );
    if (result.newBest) {
      scoreCol.append(
        el(
          'div',
          {
            font: `800 clamp(14px, 2.8vw, 18px)/1 ${FONT}`,
            letterSpacing: '0.18em',
            color: FLIR.accent,
            textShadow: `0 0 10px ${FLIR.accent}`,
          },
          'NEW BEST!',
        ),
      );
    }
    gradeRow.append(gradeBadge, scoreCol);

    // ── Stats panel — aligned label/value rows ──────────────────────────────
    const panel = makePanel({
      display: 'grid',
      gridTemplateColumns: 'auto auto',
      gap: '10px 36px',
      minWidth: 'min(360px, 86vw)',
    });
    const ffColor = s.friendliesHit > 0 ? FLIR.warn : FLIR.green;
    const rows: ReadonlyArray<readonly [string, string, string]> = [
      ['KILLS', `${s.kills}`, FLIR.green],
      ['HEADSHOTS', `${s.headshots}`, FLIR.green],
      ['BEST COMBO', `x${s.bestCombo}`, FLIR.green],
      ['SCRIP BANKED', `+${s.scrip}`, FLIR.accent],
      ['SALVAGE BANKED', `+${s.salvage}`, FLIR.accent],
      ['TIME', formatTime(s.elapsedMs), FLIR.green],
      ['FRIENDLY FIRE', `${s.friendliesHit}`, ffColor],
    ];
    for (const [label, value, color] of rows) {
      panel.append(
        el(
          'div',
          {
            font: `600 clamp(13px, 2.4vw, 16px)/1 ${FONT}`,
            letterSpacing: '0.06em',
            color: FLIR.greenDim,
          },
          label,
        ),
        el(
          'div',
          {
            font: `700 clamp(15px, 2.8vw, 20px)/1 ${FONT}`,
            letterSpacing: '0.1em',
            color,
            textAlign: 'right',
          },
          value,
        ),
      );
    }

    // WIDE results body: the grade hero + the stat sheet sit side by side on a
    // desktop frame (they stack on a narrow screen) — uses the width instead of
    // a thin centre column.
    const body = el('div', {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(min(300px, 100%), 1fr))',
      gap: '18px 28px',
      width: 'min(880px, 94vw)',
      alignItems: 'center',
      justifyItems: 'center',
    });
    body.append(gradeRow, panel);
    root.append(verdict, body);

    // ── Wallet line: where the run's pay LANDED (the bank is real) ──────────
    const wallet = this.opts.wallet;
    if (wallet) {
      root.append(
        el(
          'div',
          {
            font: `700 clamp(14px, 2.6vw, 17px)/1 ${FONT}`,
            letterSpacing: '0.05em',
            color: FLIR.accent,
            textAlign: 'center',
          },
          `WALLET ${wallet.scrip} SCRIP · ${wallet.salvage} SALVAGE`,
        ),
      );
    }
    // Earnings are KEPT win or lose — say it on EVERY debrief (not just a fail)
    // so the kill → bank → upgrade → re-deploy loop is obvious, never accidental.
    root.append(
      el(
        'div',
        {
          font: `500 clamp(12px, 2.1vw, 14px)/1.5 ${FONT}`,
          letterSpacing: '0.02em',
          color: FLIR.greenDim,
          textAlign: 'center',
          maxWidth: 'min(560px, 92vw)',
        },
        'Banked win or lose — spend it in the ARMORY, gear up, re-deploy.',
      ),
    );

    // ── Earned medals row (chips). Hidden entirely if none earned ───────────
    if (medals.length > 0) {
      root.append(
        el(
          'div',
          {
            font: `600 clamp(12px, 2.2vw, 14px)/1 ${FONT}`,
            letterSpacing: '0.18em',
            color: FLIR.greenDim,
            textAlign: 'center',
          },
          'MEDALS EARNED',
        ),
      );
      const medalRow = el('div', {
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'center',
        gap: '10px',
        maxWidth: 'min(560px, 92vw)',
      });
      for (const m of medals) medalRow.append(this.makeMedalChip(m));
      root.append(medalRow);
    }

    // ── Actions ─────────────────────────────────────────────────────────────
    const actions = el('div', {
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'center',
      gap: '14px',
      marginTop: '6px',
    });
    if (this.opts.onArmory) {
      this.armoryBtn = makeButton('ARMORY', () => this.opts.onArmory?.(), { width: 'min(220px, 42vw)' });
      actions.append(this.armoryBtn.el);
    }
    if (this.opts.onRetry) {
      // One-tap re-deploy — accented on a FAILED run (it's the natural next
      // action); CONTINUE keeps the accent on a clear.
      this.retryBtn = makeButton('RETRY', () => this.opts.onRetry?.(), {
        accent: !result.cleared,
        width: 'min(220px, 42vw)',
      });
      actions.append(this.retryBtn.el);
    }
    this.continueBtn = makeButton('CONTINUE', () => this.opts.onContinue(), {
      accent: result.cleared || !this.opts.onRetry,
      width: 'min(220px, 42vw)',
    });
    actions.append(this.continueBtn.el);

    root.append(actions);
    parent.appendChild(root);
    this.root = root;
  }

  /** A single earned-medal chip: a glowing FLIR badge with its label + desc. */
  private makeMedalChip(m: Medal): HTMLDivElement {
    const chip = el('div', {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '3px',
      background: 'rgba(255,208,138,0.10)',
      border: `1px solid ${FLIR.accent}`,
      borderRadius: '8px',
      padding: '8px 14px',
      boxShadow: `0 0 10px rgba(255,208,138,0.18)`,
    });
    chip.title = m.desc;
    chip.append(
      el(
        'div',
        {
          font: `800 clamp(12px, 2.2vw, 15px)/1 ${FONT}`,
          letterSpacing: '0.12em',
          color: FLIR.accent,
          textShadow: TEXT_SHADOW_ACCENT,
          textAlign: 'center',
        },
        m.label,
      ),
      el(
        'div',
        {
          font: `500 clamp(10px, 1.8vw, 12px)/1.1 ${FONT}`,
          letterSpacing: '0.06em',
          color: FLIR.greenDim,
          textAlign: 'center',
        },
        m.desc,
      ),
    );
    return chip;
  }

  unmount(): void {
    this.continueBtn?.destroy();
    this.armoryBtn?.destroy();
    this.retryBtn?.destroy();
    this.continueBtn = null;
    this.armoryBtn = null;
    this.retryBtn = null;
    this.root?.remove();
    this.root = null;
  }
}

const TEXT_SHADOW_ACCENT = '0 0 8px rgba(255,208,138,0.5), 0 0 4px rgba(0,0,0,0.9)';
